/**
 * Changements de hauteur sans décalage visible (suites de la confirmation, SPEC CO4 / CO5 ; revue du 09/10).
 *
 * Un bloc qui naît, grandit ou rétrécit dans le flux après l'affichage déplace tout ce qui le suit. Si ce contenu est à
 * l'écran avant ou après le déplacement (le bloc « Téléchargez l'application » du pied de page quand le client a fait
 * défiler la page jusqu'aux boutons), c'est un saut de mise en page visible (CLS 0,083 mesuré à 390 × 844). Le
 * changement n'est donc fait que si le premier contenu qui suit est sous le bas de l'écran avant ET après (ou s'il n'y
 * en a pas) ; sinon on attend un défilement ou un redimensionnement (watchUntil).
 */

/** Élément tel que le lit followingContentTop (sous-ensemble de `Element`, remplaçable dans les tests). */
export interface FlowNode {
  tagName: string;
  nextElementSibling: FlowNode | null;
  parentElement: FlowNode | null;
  getBoundingClientRect(): { top: number; height: number };
}

/** Hors du flux : un élément masqué ou positionné (fixed, absolute) ne bouge pas quand le flux s'allonge. */
function isOutOfFlowInDocument(node: FlowNode): boolean {
  const style = window.getComputedStyle(node as unknown as Element);
  return style.display === 'none' || style.position === 'fixed' || style.position === 'absolute';
}

/**
 * Haut, par rapport au haut de l'écran, du premier contenu du flux qui suit `anchor` dans le document : frères
 * suivants de `anchor`, puis de chacun de ses ancêtres jusqu'à `<body>` ; éléments hors du flux et éléments sans
 * hauteur ignorés. null s'il n'y a rien après.
 */
export function followingContentTop(
  anchor: FlowNode,
  isOutOfFlow: (node: FlowNode) => boolean = isOutOfFlowInDocument,
): number | null {
  for (let node: FlowNode | null = anchor; node && node.tagName !== 'BODY' && node.tagName !== 'HTML'; node = node.parentElement) {
    for (let sibling = node.nextElementSibling; sibling; sibling = sibling.nextElementSibling) {
      if (isOutOfFlow(sibling)) continue;
      const rect = sibling.getBoundingClientRect();
      if (rect.height > 0) return rect.top;
    }
  }
  return null;
}

/**
 * Le contenu qui suit `anchor` peut-il descendre (`delta` > 0 : insertion, bloc qui grandit) ou remonter (`delta` < 0 :
 * bloc qui rétrécit ou disparaît) de `delta` px sans que rien de visible ne bouge ? Oui s'il ne bouge pas (moins d'un
 * pixel : arrondis), s'il n'y a
 * rien après, ou s'il est sous le bas de l'écran (`viewportHeight`) avant et après le déplacement. Pour une insertion
 * de hauteur inconnue, tout `delta` > 0 convient (seule la position de départ compte).
 */
export function canResizeWithoutShift(
  anchor: FlowNode,
  delta: number,
  viewportHeight: number,
  isOutOfFlow?: (node: FlowNode) => boolean,
): boolean {
  if (Math.abs(delta) < 1) return true;
  const top = followingContentTop(anchor, isOutOfFlow);
  return top === null || Math.min(top, top + delta) >= viewportHeight;
}

/**
 * Appelle `attempt` tout de suite, puis à chaque défilement, redimensionnement ou changement de taille de la page
 * (une fois par image au plus), jusqu'à ce qu'il renvoie true. Renvoie la fonction qui arrête l'attente.
 */
export function watchUntil(attempt: () => boolean): () => void {
  let frame = 0;
  let done = false;
  let resizeObserver: ResizeObserver | null = null;
  const stop = () => {
    done = true;
    if (frame) window.cancelAnimationFrame(frame);
    window.removeEventListener('scroll', schedule);
    window.removeEventListener('resize', schedule);
    resizeObserver?.disconnect();
  };
  const check = () => {
    frame = 0;
    if (!done && attempt()) stop();
  };
  function schedule() {
    if (!frame && !done) frame = window.requestAnimationFrame(check);
  }
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule);
  resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
  resizeObserver?.observe(document.body);
  check();
  return stop;
}
